import { useCallback, useEffect, useRef, useState } from "react";
import type { RequestStatus } from "../../auth";
import { listPayments } from "../services/revenueService";
import type {
  Payment,
  PaymentTabCounts,
  PaymentTabId,
} from "../types/revenue.types";
import type { DateRangeId } from "../dateRanges";

/** How many rows a page of the payments list holds. */
export const PAYMENTS_PER_PAGE = 10;

interface UseRevenueOptions {
  page: number;
  tab: PaymentTabId;
  range: DateRangeId;
}

/**
 * One page of the payments list, plus the counts for the range it sits in.
 *
 * Two things are worth pointing at, and they are the roster's two — this
 * screen asks the same kind of question of the same kind of endpoint:
 *
 * - **Rows already in hand are left alone while the next page is fetched.** A
 *   table that empties itself on every tab click flickers. The old page stays
 *   until the new one lands, and `status` is what the screen shimmers off.
 *
 * - **A late answer to a superseded request is dropped.** Clicking two tabs
 *   quickly leaves two requests out, and nothing guarantees they come back in
 *   the order they went. The request id below is what makes the last one asked
 *   for the one that wins, rather than the last one to arrive.
 */
export function useRevenue({ page, tab, range }: UseRevenueOptions) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [total, setTotal] = useState(0);
  const [tabCounts, setTabCounts] = useState<PaymentTabCounts | null>(null);
  const [status, setStatus] = useState<RequestStatus>("loading");

  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setStatus("loading");

    try {
      const result = await listPayments({
        page,
        pageSize: PAYMENTS_PER_PAGE,
        tab,
        range,
      });

      if (id !== requestId.current) return;
      setPayments(result.payments);
      setTotal(result.total);
      // Sent with every page and describing the whole range either way, so
      // they are simply taken from whichever answer arrived last.
      setTabCounts(result.tabCounts);
      setStatus("ready");
    } catch {
      if (id !== requestId.current) return;
      setStatus("failed");
    }
  }, [page, tab, range]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    payments,
    total,
    tabCounts,
    status,
    totalPages: Math.max(1, Math.ceil(total / PAYMENTS_PER_PAGE)),
    reload: load,
  };
}

export default useRevenue;
